import { useI18n } from './i18n';

/**
 * Dates, in the language the interface is actually set to.
 *
 * Seven screens called `toLocaleDateString()` with no arguments, which asks
 * the *browser* what language it is in and formats accordingly. So a studio
 * running in French printed "9/13/2026" on its rolls, its invoices and its
 * share links — the app's own language setting had no say. And the bare form
 * is ambiguous besides: 9/13 and 13/9 are the same date written two ways, and
 * only one of them is wrong in any given country.
 */
export function useDateFormat() {
  const { locale } = useI18n();
  const tag = locale === 'fr' ? 'fr-FR' : 'en-GB';

  return {
    /** "13 September 2026" — for anything a person reads once. */
    long: (value: string | number | Date): string =>
      new Date(value).toLocaleDateString(tag, { day: 'numeric', month: 'long', year: 'numeric' }),
    /** "13 Sep" — for lists, where the year is usually noise. */
    short: (value: string | number | Date): string =>
      new Date(value).toLocaleDateString(tag, { day: 'numeric', month: 'short' }),
    /** "13 Sep, 16:02" — for something that happened today or this week. */
    stamp: (value: string | number | Date): string =>
      new Date(value).toLocaleString(tag, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
  };
}
